import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import PageHeader from '../../../components/PageHeader/PageHeader';
import CompanyService from '../../../services/CompanyService';
import DebugLogsService from '../../../services/DebugLogsService';
import { getIcon } from '../../../utils/IconHandler';
import { formatDateTime } from '../../../utils/DateUtils';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const QUICK_RANGES = [
  { label: '24h', hours: 24 },
  { label: '48h', hours: 48 },
  { label: 'Semana', days: 7 },
  { label: 'Mes', days: 30 }
];

const CATEGORY_OPTIONS = [
  { value: 'web_sales', label: 'Ventas web' },
  { value: 'billing', label: 'Facturación' },
  { value: 'system', label: 'Sistema' }
];

const ADMIN_DEFAULT_LEVEL = 'ERROR';
const ADMIN_DEFAULT_RESOLVED = 'false';

const formatDate = (value) => formatDateTime(value);

const levelColor = (level) => {
  switch ((level || '').toUpperCase()) {
    case 'ERROR':
      return 'error';
    case 'WARNING':
    case 'WARN':
      return 'warning';
    case 'INFO':
      return 'info';
    default:
      return 'default';
  }
};

const compact = (value, max = 140) => {
  if (!value) return '-';
  const text = String(value);
  return text.length > max ? `${text.slice(0, max)}...` : text;
};

const prettyPrint = (value) => {
  if (!value) return '';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return String(value);
  }
};

// Botón de copiar reutilizado en los diálogos de detalle (mensaje/stacktrace/metadata
// completos): mismo patrón en los tres, "Copiar" pasa a "Copiado" dos segundos y vuelve solo.
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <Button
      size="small"
      variant="text"
      startIcon={getIcon(copied ? 'checkmarkDone' : 'copy')}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // Sin acceso al portapapeles (permiso denegado, contexto no seguro): no es crítico,
          // el texto sigue disponible para seleccionar/copiar a mano en el diálogo.
        }
      }}
    >
      {copied ? 'Copiado' : 'Copiar'}
    </Button>
  );
};

const MonoBlock = ({ text }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1.5,
      bgcolor: 'grey.100',
      borderRadius: 1,
      fontSize: 12,
      fontFamily: 'monospace',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      maxHeight: 320,
      overflow: 'auto'
    }}
  >
    {text || '-'}
  </Box>
);

const CoreDebugPage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [level, setLevel] = useState(ADMIN_DEFAULT_LEVEL);
  const [resolved, setResolved] = useState(ADMIN_DEFAULT_RESOLVED);
  const [category, setCategory] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [event, setEvent] = useState('');
  const [action, setAction] = useState('');
  const [orderId, setOrderId] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [checkoutDetailOpen, setCheckoutDetailOpen] = useState(false);
  const [checkoutDetailGroup, setCheckoutDetailGroup] = useState(null);
  const [checkoutDetailRows, setCheckoutDetailRows] = useState([]);
  const [checkoutDetailLoading, setCheckoutDetailLoading] = useState(false);
  const [errorDetailRow, setErrorDetailRow] = useState(null);
  const [auditDetailRow, setAuditDetailRow] = useState(null);

  useEffect(() => {
    const loadCompanies = async () => {
      const data = await CompanyService.getCompanies(1, 100000, '');
      setCompanies(data);
      setSelectedCompany(data[0] || null);
    };
    loadCompanies();
  }, []);

  const baseParams = useMemo(() => ({
    tenantId: selectedCompany?.id,
    page: page + 1,
    pageSize,
    search: search.trim() || undefined
  }), [selectedCompany, page, pageSize, search]);

  const currentParams = useMemo(() => {
    if (activeTab === 0) {
      return {
        ...baseParams,
        level: level || undefined,
        resolved: resolved === '' ? undefined : resolved,
        category: category || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        includeDeleted: includeDeleted || undefined
      };
    }
    if (activeTab === 1) {
      return {
        ...baseParams,
        action: action.trim() || undefined
      };
    }
    return {
      ...baseParams,
      level: level || undefined,
      event: event.trim() || undefined,
      orderId: orderId.trim() || undefined
    };
  }, [activeTab, baseParams, level, resolved, category, fromDate, toDate, includeDeleted, action, event, orderId]);

  const loadLogs = useCallback(async () => {
    if (!selectedCompany?.id) return;
    setLoading(true);
    try {
      const data = activeTab === 0
        ? await DebugLogsService.getErrors(currentParams)
        : activeTab === 1
          ? await DebugLogsService.getAudit(currentParams)
          : await DebugLogsService.getCheckoutGroups(currentParams);
      setRows(data?.items || []);
      setTotal(data?.total || 0);
      setError('');
    } catch (err) {
      setRows([]);
      setTotal(0);
      setError(err?.response?.data?.error_message || 'No se pudieron cargar los logs.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentParams, selectedCompany]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleTabChange = (_event, nextTab) => {
    setActiveTab(nextTab);
    setPage(0);
    setLevel((nextTab === 0 || nextTab === 2) ? ADMIN_DEFAULT_LEVEL : '');
    setResolved(nextTab === 0 ? ADMIN_DEFAULT_RESOLVED : '');
    setCategory('');
    setFromDate('');
    setToDate('');
    setIncludeDeleted(false);
    setEvent('');
    setAction('');
    setOrderId('');
  };

  const handleClearFilters = () => {
    setPage(0);
    setSearch('');
    setLevel('');
    setResolved('');
    setCategory('');
    setFromDate('');
    setToDate('');
    setIncludeDeleted(false);
    setEvent('');
    setAction('');
    setOrderId('');
  };

  const handleExport = async () => {
    if (!selectedCompany?.id) return;
    setExporting(true);
    try {
      const params = { ...currentParams };
      delete params.page;
      delete params.pageSize;
      if (activeTab === 0) await DebugLogsService.exportErrors(params);
      if (activeTab === 1) await DebugLogsService.exportAudit(params);
      if (activeTab === 2) await DebugLogsService.exportCheckout(params);
    } finally {
      setExporting(false);
    }
  };

  const handleResolvedChange = async (row, checked) => {
    await DebugLogsService.updateErrorResolved(selectedCompany.id, row.id, checked);
    setRows((prev) => prev.map((item) => (item.id === row.id ? { ...item, resolved: checked } : item)));
  };

  const applyQuickRange = (range) => {
    const now = new Date();
    const start = new Date(now);
    if (range.hours) start.setHours(start.getHours() - range.hours);
    if (range.days) start.setDate(start.getDate() - range.days);
    setFromDate(start.toISOString().slice(0, 16));
    setToDate(now.toISOString().slice(0, 16));
    setPage(0);
  };

  const handleResolveVisible = async () => {
    if (!selectedCompany?.id || activeTab !== 0 || rows.length === 0) return;
    await DebugLogsService.resolveErrors(selectedCompany.id, rows.map((row) => row.id));
    await loadLogs();
  };

  const handleDeleteVisible = async () => {
    if (!selectedCompany?.id || activeTab !== 0 || rows.length === 0) return;
    await DebugLogsService.deleteErrors(selectedCompany.id, rows.map((row) => row.id));
    await loadLogs();
  };

  const handleDeleteAllFiltered = async () => {
    if (!selectedCompany?.id || activeTab !== 0) return;
    if (!window.confirm('¿Marcar como resueltos y ocultar todos los issues no ocultos de esta empresa?')) return;
    await DebugLogsService.deleteErrors(selectedCompany.id, null);
    await loadLogs();
  };

  const handleOpenCheckoutDetail = async (group) => {
    if (!selectedCompany?.id) return;
    setCheckoutDetailGroup(group);
    setCheckoutDetailRows([]);
    setCheckoutDetailOpen(true);
    setCheckoutDetailLoading(true);
    try {
      const data = await DebugLogsService.getCheckoutGroupDetails({
        tenantId: selectedCompany.id,
        orderId: group.orderId || undefined,
        checkoutIdempotencyKey: group.checkoutIdempotencyKey || undefined
      });
      setCheckoutDetailRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setCheckoutDetailRows([]);
    } finally {
      setCheckoutDetailLoading(false);
    }
  };

  const renderFilters = () => (
    <Stack
      direction="row"
      spacing={2}
      useFlexGap
      alignItems="center"
      sx={{ mb: 2, flexWrap: 'wrap' }}
    >
      <Autocomplete
        options={companies}
        value={selectedCompany}
        onChange={(_event, value) => {
          setSelectedCompany(value);
          setPage(0);
        }}
        getOptionLabel={(option) => option ? `${option.name} (#${option.id})` : ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        size="small"
        sx={{ flex: '1 1 280px', minWidth: { xs: '100%', sm: 280 } }}
        renderInput={(params) => <TextField {...params} label="Empresa" />}
      />
      <TextField
        label="Buscar"
        value={search}
        onChange={(eventValue) => {
          setSearch(eventValue.target.value);
          setPage(0);
        }}
        size="small"
        sx={{ flex: '1 1 260px', minWidth: { xs: '100%', sm: 240 } }}
      />
      {(activeTab === 0 || activeTab === 2) && (
        <FormControl size="small" sx={{ flex: '0 1 150px', minWidth: { xs: '100%', sm: 140 } }}>
          <InputLabel>Nivel</InputLabel>
          <Select label="Nivel" value={level} onChange={(e) => { setLevel(e.target.value); setPage(0); }}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="INFO">INFO</MenuItem>
            <MenuItem value="WARNING">WARNING</MenuItem>
            <MenuItem value="ERROR">ERROR</MenuItem>
          </Select>
        </FormControl>
      )}
      {activeTab === 0 && (
        <>
          <FormControl size="small" sx={{ flex: '0 1 150px', minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Estado</InputLabel>
            <Select label="Estado" value={resolved} onChange={(e) => { setResolved(e.target.value); setPage(0); }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="false">Pendientes</MenuItem>
              <MenuItem value="true">Resueltos</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: '0 1 170px', minWidth: { xs: '100%', sm: 160 } }}>
            <InputLabel>Categoría</InputLabel>
            <Select label="Categoría" value={category} onChange={(e) => { setCategory(e.target.value); setPage(0); }}>
              <MenuItem value="">Todas</MenuItem>
              {CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Desde" type="datetime-local" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(0); }} size="small" InputLabelProps={{ shrink: true }} sx={{ flex: '0 1 190px', minWidth: { xs: '100%', sm: 180 } }} />
          <TextField label="Hasta" type="datetime-local" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(0); }} size="small" InputLabelProps={{ shrink: true }} sx={{ flex: '0 1 190px', minWidth: { xs: '100%', sm: 180 } }} />
          <Stack direction="row" spacing={1}>
            {QUICK_RANGES.map((range) => (
              <Button key={range.label} size="small" variant="outlined" onClick={() => applyQuickRange(range)}>
                {range.label}
              </Button>
            ))}
          </Stack>
          <FormControlLabel
            control={<Checkbox checked={includeDeleted} onChange={(e) => { setIncludeDeleted(e.target.checked); setPage(0); }} />}
            label="Ver ocultos"
          />
        </>
      )}
      {activeTab === 1 && (
        <TextField label="Acción" value={action} onChange={(e) => { setAction(e.target.value); setPage(0); }} size="small" sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }} />
      )}
      {activeTab === 2 && (
        <>
          <TextField label="Evento" value={event} onChange={(e) => { setEvent(e.target.value); setPage(0); }} size="small" sx={{ flex: '0 1 150px', minWidth: { xs: '100%', sm: 140 } }} />
          <TextField label="Order ID" value={orderId} onChange={(e) => { setOrderId(e.target.value); setPage(0); }} size="small" sx={{ flex: '0 1 150px', minWidth: { xs: '100%', sm: 140 } }} />
        </>
      )}
      <Box flex={1} />
      <Button variant="outlined" onClick={handleClearFilters} disabled={loading} sx={{ flex: '0 0 auto', minWidth: 140 }}>
        Limpiar filtros
      </Button>
      {activeTab === 0 && (
        <>
          <Button variant="outlined" onClick={handleResolveVisible} disabled={loading || rows.length === 0} sx={{ flex: '0 0 auto' }}>
            Resolver visibles
          </Button>
          <Button variant="outlined" color="warning" onClick={handleDeleteVisible} disabled={loading || rows.length === 0} sx={{ flex: '0 0 auto' }}>
            Ocultar visibles
          </Button>
          <Button variant="outlined" color="error" onClick={handleDeleteAllFiltered} disabled={loading || !selectedCompany} sx={{ flex: '0 0 auto' }}>
            Borrar todo
          </Button>
        </>
      )}
      <Button variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : getIcon('download')} onClick={handleExport} disabled={exporting || !selectedCompany} sx={{ flex: '0 0 auto', minWidth: 128 }}>
        Export
      </Button>
      <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : getIcon('refresh')} onClick={loadLogs} disabled={loading || !selectedCompany} sx={{ flex: '0 0 auto', minWidth: 140 }}>
        Actualizar
      </Button>
    </Stack>
  );

  const renderErrorsTable = () => (
    <TableBody>
      {rows.map((row) => (
        <TableRow key={row.id} hover onClick={() => setErrorDetailRow(row)} sx={{ cursor: 'pointer' }}>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.createdAt)}</TableCell>
          <TableCell><Chip size="small" color={levelColor(row.level)} label={row.level} variant="outlined" /></TableCell>
          <TableCell sx={{ maxWidth: 520 }}>
            <Typography variant="body2" noWrap title={row.message}>
              {row.message || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {CATEGORY_OPTIONS.find((option) => option.value === row.category)?.label || row.category || 'Sistema'}
              {row.source ? ` · ${row.source}` : ''}
            </Typography>
          </TableCell>
          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
            {row.deletedAt ? (
              <Chip size="small" label="Oculto" />
            ) : (
              <Switch checked={!!row.resolved} onChange={(e) => handleResolvedChange(row, e.target.checked)} />
            )}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  const renderAuditTable = () => (
    <TableBody>
      {rows.map((row) => (
        <TableRow key={row.id} hover onClick={() => setAuditDetailRow(row)} sx={{ cursor: 'pointer' }}>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.createdAt)}</TableCell>
          <TableCell>{row.userEmail}</TableCell>
          <TableCell>{row.action}</TableCell>
          <TableCell sx={{ maxWidth: 480 }}>
            <Typography variant="body2" noWrap title={row.description}>
              {row.description || '-'}
            </Typography>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  const renderCheckoutTable = () => (
    <TableBody>
      {rows.map((row) => (
        <TableRow key={row.id} hover onClick={() => handleOpenCheckoutDetail(row)} sx={{ cursor: 'pointer' }}>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.lastAt)}</TableCell>
          <TableCell>{row.orderId || '-'}</TableCell>
          <TableCell>{row.eventCount}</TableCell>
          <TableCell>{row.lastEvent}</TableCell>
          <TableCell><Chip size="small" color={levelColor(row.level)} label={row.level} variant="outlined" /></TableCell>
          <TableCell sx={{ maxWidth: 420 }}>
            <Typography variant="body2" noWrap title={row.message}>
              {row.message || '-'}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Tooltip title="Ver traza">
              <IconButton size="small" onClick={() => handleOpenCheckoutDetail(row)}>
                {getIcon('eye')}
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  const emptyRow = (colSpan) => (
    <TableBody>
      <TableRow>
        <TableCell colSpan={colSpan} align="center">
          {loading ? <CircularProgress size={24} /> : 'Sin logs para los filtros seleccionados.'}
        </TableCell>
      </TableRow>
    </TableBody>
  );

  return (
    <Box p={3}>
      <PageHeader title="Debug" subtitle="Errores, auditoría y trazas de checkout por empresa" />
      <Paper sx={{ mt: 2, p: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab label="Errors" />
          <Tab label="Audit logs" />
          <Tab label="Checkout logs" />
        </Tabs>
        {renderFilters()}
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Tocá una fila para ver el detalle completo.
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: activeTab === 2 ? 900 : activeTab === 0 ? 700 : 750 }}>
            <TableHead>
              {activeTab === 0 && (
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Nivel</TableCell>
                  <TableCell>Mensaje</TableCell>
                  <TableCell align="center">Resuelto</TableCell>
                </TableRow>
              )}
              {activeTab === 1 && (
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Acción</TableCell>
                  <TableCell>Descripción</TableCell>
                </TableRow>
              )}
              {activeTab === 2 && (
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Eventos</TableCell>
                  <TableCell>Último evento</TableCell>
                  <TableCell>Nivel</TableCell>
                  <TableCell>Mensaje</TableCell>
                  <TableCell align="right">Detalle</TableCell>
                </TableRow>
              )}
            </TableHead>
            {rows.length === 0
              ? emptyRow(activeTab === 0 ? 4 : activeTab === 1 ? 4 : 7)
              : activeTab === 0
                ? renderErrorsTable()
                : activeTab === 1
                  ? renderAuditTable()
                  : renderCheckoutTable()}
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(eventValue) => {
            setPageSize(parseInt(eventValue.target.value, 10));
            setPage(0);
          }}
        />
        <Typography variant="caption" color="text.secondary">
          Los exports usan los mismos filtros activos y descargan todo el resultado filtrado.
        </Typography>
      </Paper>
      <Dialog open={checkoutDetailOpen} onClose={() => setCheckoutDetailOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>
          Traza checkout {checkoutDetailGroup?.orderId ? `orden #${checkoutDetailGroup.orderId}` : checkoutDetailGroup?.checkoutIdempotencyKey || ''}
        </DialogTitle>
        <DialogContent>
          {checkoutDetailGroup && (
            <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }}>
              <Typography variant="body2"><strong>Pago:</strong> {checkoutDetailGroup.paymentMethod || '-'}</Typography>
              <Typography variant="body2"><strong>Entrega:</strong> {checkoutDetailGroup.deliveryType || '-'}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {checkoutDetailGroup.customerEmail || '-'}</Typography>
              <Typography variant="body2"><strong>Factura:</strong> {checkoutDetailGroup.invoiceId || '-'}</Typography>
            </Stack>
          )}
          {checkoutDetailLoading ? (
            <Box py={4} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1300 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Evento</TableCell>
                    <TableCell>Nivel</TableCell>
                    <TableCell>Order</TableCell>
                    <TableCell>Pago</TableCell>
                    <TableCell>Entrega</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Mensaje</TableCell>
                    <TableCell>Metadata</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {checkoutDetailRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatDate(row.createdAt)}</TableCell>
                      <TableCell>{row.event}</TableCell>
                      <TableCell><Chip size="small" color={levelColor(row.level)} label={row.level} variant="outlined" /></TableCell>
                      <TableCell>{row.orderId || '-'}</TableCell>
                      <TableCell>{row.paymentMethod || '-'}</TableCell>
                      <TableCell>{row.deliveryType || '-'}</TableCell>
                      <TableCell>{row.customerEmail || '-'}</TableCell>
                      <TableCell>{compact(row.message, 180)}</TableCell>
                      <TableCell>{compact(row.metadata, 320)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!errorDetailRow} onClose={() => setErrorDetailRow(null)} maxWidth="md" fullWidth>
        {errorDetailRow && (
          <>
            <DialogTitle>
              <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
                <Chip size="small" color={levelColor(errorDetailRow.level)} label={errorDetailRow.level} variant="outlined" />
                <Typography variant="body2" color="text.secondary">
                  {formatDate(errorDetailRow.createdAt)} · {CATEGORY_OPTIONS.find((option) => option.value === errorDetailRow.category)?.label || errorDetailRow.category || 'Sistema'}
                </Typography>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                {errorDetailRow.source && (
                  <Typography variant="body2"><strong>Fuente:</strong> {errorDetailRow.source}</Typography>
                )}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">Mensaje</Typography>
                    <CopyButton text={errorDetailRow.message} />
                  </Stack>
                  <MonoBlock text={errorDetailRow.message} />
                </Box>
                {errorDetailRow.stacktrace && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">Stacktrace</Typography>
                      <CopyButton text={errorDetailRow.stacktrace} />
                    </Stack>
                    <MonoBlock text={errorDetailRow.stacktrace} />
                  </Box>
                )}
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2">Estado:</Typography>
                  {errorDetailRow.deletedAt ? (
                    <Chip size="small" label="Oculto" />
                  ) : (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!errorDetailRow.resolved}
                          onChange={(e) => {
                            handleResolvedChange(errorDetailRow, e.target.checked);
                            setErrorDetailRow((prev) => prev && { ...prev, resolved: e.target.checked });
                          }}
                        />
                      }
                      label={errorDetailRow.resolved ? 'Resuelto' : 'Pendiente'}
                    />
                  )}
                </Stack>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
      <Dialog open={!!auditDetailRow} onClose={() => setAuditDetailRow(null)} maxWidth="md" fullWidth>
        {auditDetailRow && (
          <>
            <DialogTitle>
              <Typography variant="body2" color="text.secondary">
                {formatDate(auditDetailRow.createdAt)} · {auditDetailRow.userEmail} · {auditDetailRow.action}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2">Descripción</Typography>
                  <MonoBlock text={auditDetailRow.description} />
                </Box>
                {auditDetailRow.metadata && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">Metadata</Typography>
                      <CopyButton text={auditDetailRow.metadata} />
                    </Stack>
                    <MonoBlock text={prettyPrint(auditDetailRow.metadata)} />
                  </Box>
                )}
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CoreDebugPage;
